import { Link } from "react-router-dom";

const Disclaimer = () => {
  return (
    <div data-testid="disclaimer-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-primary/5 py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-secondary">
              Disclaimer
            </h1>
            <p className="text-slate-600 mt-4">
              Last updated: December 2024
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-slate max-w-none">
            <p className="text-lg text-slate-600 mb-8">
              The information provided on the CartNexa website and through our services is for general informational purposes only. Please read this disclaimer carefully before using our website or services.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              1. General Information
            </h2>
            <p className="text-slate-600 mb-6">
              The content on the CartNexa website, including but not limited to text, graphics, images, and other material, is provided for general informational purposes only. It is not intended to be a substitute for professional advice or specific recommendations for your situation.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              2. Service Limitations
            </h2>
            <p className="text-slate-600 mb-4">
              While CartNexa strives to provide high-quality cleaning services, please be aware of the following limitations:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li><strong>Results May Vary:</strong> Cleaning results depend on factors including the condition of surfaces, type of materials, age of stains, and environmental conditions.</li>
              <li><strong>Not All Stains Are Removable:</strong> Some stains, damage, or discoloration may be permanent and cannot be fully removed through cleaning.</li>
              <li><strong>Surface Sensitivity:</strong> Certain materials may react differently to cleaning products. We recommend informing us of any sensitive surfaces.</li>
              <li><strong>Pre-existing Conditions:</strong> We are not responsible for damage or issues that existed prior to our service.</li>
              <li><strong>Pest Control:</strong> Our cleaning services do not include pest control or extermination services.</li>
            </ul>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              3. Pricing Estimates
            </h2>
            <p className="text-slate-600 mb-6">
              All pricing information on our website is provided as general estimates only. Final pricing is determined after assessment of the actual property and may vary based on size, condition, specific requirements, and other factors. Online quotes are not binding until confirmed by our team.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              4. Testimonials and Reviews
            </h2>
            <p className="text-slate-600 mb-6">
              Testimonials and reviews displayed on our website reflect the personal experiences of individual customers. Results and experiences may vary. These testimonials are not intended to guarantee that all customers will achieve the same results.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              5. Website Accuracy
            </h2>
            <p className="text-slate-600 mb-4">
              We make every effort to ensure the information on our website is accurate and up-to-date. However:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li>We do not warrant that the website content is complete, accurate, or current</li>
              <li>Information may change without notice</li>
              <li>We are not responsible for errors or omissions in the content</li>
              <li>The website may contain typographical errors or technical inaccuracies</li>
            </ul>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              6. Third-Party Links
            </h2>
            <p className="text-slate-600 mb-6">
              Our website may contain links to third-party websites or services. These links are provided for convenience only. CartNexa does not endorse, control, or assume responsibility for the content, privacy policies, or practices of any third-party websites.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              7. Professional Advice
            </h2>
            <p className="text-slate-600 mb-6">
              The information on this website should not be considered professional advice for specific cleaning needs. For specialized cleaning requirements (such as hazardous material cleanup, fire damage restoration, or biohazard cleaning), please consult with appropriate licensed professionals.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              8. Limitation of Liability
            </h2>
            <p className="text-slate-600 mb-6">
              To the maximum extent permitted by law, CartNexa and its owners, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of our website or services.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              9. Service Availability
            </h2>
            <p className="text-slate-600 mb-6">
              Service availability is subject to scheduling, staff availability, and geographic location. We reserve the right to decline service requests at our discretion. Service areas listed on our website are general guidelines and may not include all locations.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              10. Changes to Disclaimer
            </h2>
            <p className="text-slate-600 mb-6">
              CartNexa reserves the right to modify this disclaimer at any time without prior notice. Changes will be effective immediately upon posting to the website. Your continued use of our website or services constitutes acceptance of the updated disclaimer.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              11. Contact Us
            </h2>
            <p className="text-slate-600 mb-4">
              If you have any questions about this disclaimer, please contact us:
            </p>
            <div className="bg-slate-50 rounded-xl p-6 mb-6">
              <p className="text-slate-700 font-medium">CartNexa</p>
              <p className="text-slate-600">783 Meadowbrook Lane</p>
              <p className="text-slate-600">Atlanta, GA 30303</p>
              <p className="text-slate-600 mt-2">
                Email: <a href="mailto:support@cartnexa.store" className="text-primary hover:underline">support@cartnexa.store</a>
              </p>
              <p className="text-slate-600">
                Phone: <a href="tel:+18717871311" className="text-primary hover:underline">+1-871-7871311</a>
              </p>
            </div>

            <div className="mt-12 pt-8 border-t border-slate-200">
              <Link to="/" className="text-primary font-medium hover:underline">
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Disclaimer;
